// Utilities for exporting admin datasets to CSV and PDF.
// CSV is generated natively (no deps). PDF uses jspdf + jspdf-autotable.
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/** Safely resolve a possibly-nested value and stringify it for a report cell. */
export const cellValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value instanceof Date) return value.toLocaleString();
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '';
    }
  }
  return String(value);
};

/** Read a dotted path (e.g. "contacts.email") from an object. */
export const getByPath = (obj, path) => {
  if (!obj || !path) return undefined;
  return String(path)
    .split('.')
    .reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
};

/**
 * Build a 2D matrix (header row + data rows) from records + column defs.
 * @param {Array<object>} rows
 * @param {Array<{ header: string, accessor: string|function }>} columns
 */
export const buildMatrix = (rows, columns) => {
  const header = columns.map((c) => c.header);
  const body = (rows || []).map((row) =>
    columns.map((col) => {
      const raw = typeof col.accessor === 'function' ? col.accessor(row) : getByPath(row, col.accessor);
      return cellValue(col.format ? col.format(raw, row) : raw);
    })
  );
  return { header, body };
};

const escapeCsvCell = (value) => {
  const s = value == null ? '' : String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const timestamp = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
};

/**
 * Export rows to CSV and trigger a browser download.
 * @param {Array<object>} rows
 * @param {Array<{header,accessor}>} columns
 * @param {string} baseName - filename without extension
 */
export const exportToCsv = (rows, columns, baseName = 'report') => {
  const { header, body } = buildMatrix(rows, columns);
  const lines = [header, ...body].map((cells) => cells.map(escapeCsvCell).join(','));
  // Prepend BOM so Excel opens UTF-8 correctly.
  const csv = '\uFEFF' + lines.join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, `${baseName}-${timestamp()}.csv`);
};

/**
 * Export rows to a formatted PDF table and trigger a browser download.
 * @param {Array<object>} rows
 * @param {Array<{header,accessor}>} columns
 * @param {string} baseName
 * @param {{ title?: string, subtitle?: string }} [opts]
 */
export const exportToPdf = (rows, columns, baseName = 'report', opts = {}) => {
  const { header, body } = buildMatrix(rows, columns);
  const orientation = columns.length > 6 ? 'landscape' : 'portrait';
  const doc = new jsPDF({ orientation, unit: 'pt', format: 'a4' });

  const title = opts.title || 'TenantScore Report';
  const generatedAt = new Date().toLocaleString();

  doc.setFontSize(16);
  doc.setTextColor(2, 35, 106);
  doc.text(title, 40, 40);

  doc.setFontSize(10);
  doc.setTextColor(120, 120, 120);
  const metaLine = opts.subtitle
    ? `${opts.subtitle}  •  ${rows?.length || 0} records  •  Generated ${generatedAt}`
    : `${rows?.length || 0} records  •  Generated ${generatedAt}`;
  doc.text(metaLine, 40, 58);

  autoTable(doc, {
    head: [header],
    body,
    startY: 74,
    styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
    headStyles: { fillColor: [2, 35, 106], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { left: 40, right: 40 },
    didDrawPage: (data) => {
      const pageCount = doc.internal.getNumberOfPages();
      const pageCurrent = doc.internal.getCurrentPageInfo().pageNumber;
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Page ${pageCurrent} of ${pageCount}`,
        data.settings.margin.left,
        doc.internal.pageSize.getHeight() - 20
      );
    },
  });

  doc.save(`${baseName}-${timestamp()}.pdf`);
};
